import React, { FC } from 'react';
import Autocomplete from '@/components/elements/AutoComplete';
import { ELEMENTS_PROPS } from '@/constant/global/global.constant';
import { FieldLabel, Text } from '@/components/atoms/AppText';
import { APP_BORDER, APP_SEMANTIC } from '@/constant/theme/colors.constant';
import FieldError from './FieldError';

export interface DropdownOption {
  label: string;
  value: string;
  description?: string;
}

interface ConfigDropdownProps {
  label: string;
  value: string;
  /** Static options list. Ignored when `dynamicLists` is provided. */
  options?: DropdownOption[];
  /**
   * Dynamic lists from an external source (e.g. Redux).
   * When provided, `options` is ignored and `handleSearch` is called on input changes.
   * Each item must have an `id` field and the display field set by `nameKey`.
   */
  dynamicLists?: any[];
  /** Key used to display the name from dynamicLists items (default: 'name') */
  nameKey?: string;
  /** Called on input change when using dynamicLists */
  handleSearch?: (query: string) => void;
  /** Called when an item from dynamicLists is selected (receives the full item object) */
  onDynamicSelect?: (item: any) => void;
  onChange?: (value: string) => void;
  placeholder?: string;
  /** Kept for API compatibility — unused (Autocomplete uses brand colours) */
  activeBorderClass?: string;
  iconColor?: string;
  isRequired?: boolean;
  itemSelectedBg?: string;
  icon?: React.ComponentType<{ className?: string }>;
  enableSearch?: boolean;
  /** Show red border + message when truthy */
  error?: string | boolean;
  errorMessage?: string;
  /** Called when the input loses focus without selecting */
  onBlur?: () => void;
  /** Accent text-color class for dropdown header/label/chevron (default: 'text-primary') */
  accentColor?: string;
  /** Tailwind gradient classes for selected item bg (default: 'from-primary via-secondary to-tertiary') */
  selectedGradient?: string;
  /** Focus border class on the input (default: 'focus:border-primary') */
  focusBorderColor?: string;
  /** Accent bg class for badges and empty-state fills (e.g. 'bg-blue-500/10') */
  accentBg?: string;
  /** Pre-selected value object for dynamicLists mode */
  selectedValue?: any;
  /** data-testid forwarded to the inner Autocomplete */
  dataTestId?: string;
  /** Disables interaction and dims the dropdown */
  disabled?: boolean;
}

const ConfigDropdown: FC<ConfigDropdownProps> = ({
  dataTestId,
  label,
  value,
  options = [],
  dynamicLists,
  nameKey = 'name',
  handleSearch,
  onDynamicSelect,
  onChange,
  placeholder = ELEMENTS_PROPS.CONFIG_DROPDOWN.DEFAULT_PLACEHOLDER,
  icon,
  iconColor = 'text-primary',
  isRequired,
  error,
  errorMessage,
  onBlur,
  accentColor,
  selectedGradient,
  focusBorderColor,
  accentBg,
  enableSearch,
  selectedValue,
  disabled = false,
}) => {
  const isDynamic = !!dynamicLists;

  // Static mode: map DropdownOption[] → Autocomplete list format
  const staticLists = options.map((opt) => ({
    id: opt.value,
    name: opt.label,
    value: opt.value,
    description: opt.description,
  }));

  const lists = isDynamic ? dynamicLists! : staticLists;
  const selectedObj = isDynamic
    ? (selectedValue ?? null)
    : (lists.find((item) => item.value === value) ?? null);

  const hasError = !!error;
  const msg = typeof error === 'string' ? error : errorMessage;
  const Icon = icon as React.ComponentType<{ className?: string }> | undefined;

  return (
    <div>
      {/* Label — matches ConfigInput style */}
      <label className="flex items-center gap-2 mb-1">
        {Icon && (
          <span
            className={`inline-flex items-center justify-center w-4 h-4 flex-shrink-0 ${iconColor}`}
          >
            <Icon />
          </span>
        )}
        <FieldLabel>{label}</FieldLabel>
        <Text
          as="span"
          size="xl"
          color="danger"
          className={`${isRequired ? '' : 'invisible'}`}
        >
          *
        </Text>
      </label>

      <div className={disabled ? 'relative opacity-50' : ''}>
        {disabled && <div className="absolute inset-0 z-10 cursor-not-allowed" />}
        <Autocomplete
          label={label}
          dataTestId={dataTestId}
          hideTopLabel
          placeholder={placeholder}
          lists={lists}
          selectedValue={selectedObj}
          nameKey={isDynamic ? nameKey : 'name'}
          error={hasError}
          borderColor={hasError ? APP_SEMANTIC.danger.border : APP_BORDER.default}
          accentColor={accentColor}
          selectedGradient={selectedGradient}
          focusBorderColor={focusBorderColor}
          accentBg={accentBg}
          enableSearch={enableSearch}
          handleSelectedValue={(selected: any) => {
            if (isDynamic) {
              onDynamicSelect?.(selected);
            } else {
              onChange?.(selected?.value ?? selected ?? '');
            }
          }}
          onBlur={onBlur}
          handleSearch={isDynamic && handleSearch ? handleSearch : () => { }}
          handleFetchMore={() => { }}
        />
      </div>
      <div className="-mt-1">
        <FieldError message={msg} />
      </div>
    </div>
  );
};

export default ConfigDropdown;
